import { useEffect, useRef } from 'react'
import { useFlags, camelCaseKeys } from 'launchdarkly-react-client-sdk'
import type { ClientLDFlagSet } from 'types/launchdarkly-js-client-sdk'
import { logError } from 'lib/tracking/datadog'

const logMissingFlags = (expectedFlags: ClientLDFlagSet, receivedFlags: ReturnType<typeof useFlags>) => {
  const missing = Object.keys(camelCaseKeys(expectedFlags)).filter((key) => !(key in receivedFlags))

  if (!missing.length) {
    return
  }

  const errorMessage = `Feature flag${missing.length > 1 ? 's' : ''} <${missing.join(', ')}> not found in LaunchDarkly`

  logError(new Error(errorMessage), { expectedFlags, receivedFlags })

  if (process.env.NODE_ENV === 'development') {
    console.error(errorMessage)
  }
}

/**
 * Hook that validates expected LaunchDarkly flags against received flags from LaunchDarkly.
 * Logs an error once per session if any expected flags are missing.
 *
 * @param expectedFlags - The flags object that defines which flags should exist in LaunchDarkly
 */
export const useValidateLDFlags = (expectedFlags?: ClientLDFlagSet) => {
  const flags = useFlags()
  const hasLoggedRef = useRef(false)

  useEffect(() => {
    if (expectedFlags && !hasLoggedRef.current && Object.keys(flags).length > 0) {
      logMissingFlags(expectedFlags, flags)
      hasLoggedRef.current = true
    }
  }, [expectedFlags, flags])
}
